import React, { useEffect, useMemo, useRef, useState } from "react";
import { assetUrl } from "@/lib/api";
import { getThemeMeta, PREMIUM_THEME_KEYS } from "@/components/public/ThemeWrap";

/**
 * WheelCanvas - high-performance HTML canvas spin wheel.
 *
 * Props:
 *   slices: [{ label, color_hex, image_url, is_better_luck }]
 *   logoUrl: string (optional, drawn in center)
 *   primaryColor: brand primary (used for pointer/ring)
 *   theme: string — theme slug; when it's a premium theme and `useThemeStyle`
 *          is true, the wheel auto-styles to match the reference design
 *          (alternating theme-tint & white slices, theme-colored center dot
 *          + outer ring).
 *   useThemeStyle: boolean (default true) — when true and theme is premium,
 *          ignores per-slice `color_hex` in favor of the themed pattern.
 *          Vendors can disable this in the wizard to keep their own colors.
 *   spinningTo: index | null - when set, animates to that slice
 *   spinDuration: ms (default 5200)
 *   onSpinEnd: () => void
 *   size: pixel size (defaults to responsive via parent w/h)
 */
export const WheelCanvas = ({
  slices = [],
  logoUrl,
  primaryColor = "#0F6B73",
  theme,
  useThemeStyle = true,
  spinningTo = null,
  spinDuration = 5200,
  onSpinEnd,
  testId = "wheel-canvas",
}) => {
  const canvasRef = useRef(null);
  const logoImgRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef({ angle: 0 });
  const [, force] = useState(0);

  // Resolve the active theme tint (used by both the styled palette and the
  // center hub / outer ring when the premium auto-style is on).
  const themeMeta = useMemo(() => getThemeMeta(theme), [theme]);
  const isPremiumTheme = !!theme && PREMIUM_THEME_KEYS.includes(theme);
  const usePremiumStyle = useThemeStyle && isPremiumTheme;
  const themeTint = themeMeta?.tint || primaryColor;
  // Effective primary used for pointer/ring/hub-text.
  const effectivePrimary = usePremiumStyle ? themeTint : primaryColor;

  const palette = useMemo(() => {
    // 1) Premium auto-style → alternating tint/white pattern, matching the
    //    reference designs in object storage. Vendor `color_hex` is honored
    //    only when the vendor explicitly disables theme styling.
    if (usePremiumStyle) {
      return slices.map((s, i) =>
        s.is_better_luck ? "#9CA3AF" : (i % 2 === 0 ? themeTint : "#FFFFFF")
      );
    }
    // 2) Non-premium fallback: alternating Gold / Dark default, with per-slice
    //    color_hex taking precedence when set.
    const base = [
      "#F5A800", "#111111", "#F5A800", "#111111",
      "#F5A800", "#111111", "#F5A800", "#111111",
      "#F5A800", "#111111", "#F5A800", "#111111",
    ];
    return slices.map((s, i) => s.color_hex || base[i % base.length]);
  }, [slices, usePremiumStyle, themeTint]);

  // Load logo once
  useEffect(() => {
    if (!logoUrl) {
      logoImgRef.current = null;
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      logoImgRef.current = img;
      force((x) => x + 1);
    };
    img.onerror = () => {
      logoImgRef.current = null;
    };
    img.src = assetUrl(logoUrl);
  }, [logoUrl]);

  // Drawing helpers
  const draw = (angle) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    const W = Math.max(rect.width, 280);
    if (canvas.width !== W * dpr) {
      canvas.width = W * dpr;
      canvas.height = W * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const size = W;
    const cx = size / 2;
    const cy = size / 2;
    const radius = size / 2 - 6;
    ctx.clearRect(0, 0, size, size);

    const n = Math.max(slices.length, 1);
    const arc = (Math.PI * 2) / n;

    // outer glow ring (theme tint when premium, otherwise primaryColor)
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, radius + 4, 0, Math.PI * 2);
    ctx.lineWidth = usePremiumStyle ? 10 : 8;
    ctx.strokeStyle = effectivePrimary;
    ctx.globalAlpha = usePremiumStyle ? 0.95 : 0.18;
    ctx.stroke();
    ctx.restore();

    // slices
    for (let i = 0; i < n; i++) {
      const start = angle + i * arc;
      const end = start + arc;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, radius, start, end);
      ctx.closePath();
      ctx.fillStyle = palette[i];
      ctx.fill();
      ctx.lineWidth = usePremiumStyle ? 1 : 2;
      ctx.strokeStyle = usePremiumStyle ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.55)";
      ctx.stroke();

      // label
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(start + arc / 2);
      ctx.textAlign = "right";
      ctx.fillStyle = readableTextColor(palette[i]);
      ctx.font = `600 ${Math.max(13, size * 0.04)}px var(--font-public, 'Figtree','Inter',sans-serif)`;
      const label = (slices[i] && (slices[i].label || slices[i].gift_name)) || "";
      const maxLen = 18;
      const text = label.length > maxLen ? label.slice(0, maxLen - 1) + "…" : label;
      ctx.fillText(text, radius - 16, 4);
      ctx.restore();
    }

    // center hub
    const hubR = size * 0.13;
    ctx.save();
    // soft shadow ring
    ctx.beginPath();
    ctx.arc(cx, cy, hubR + 6, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0,0,0,0.2)";
    ctx.fill();
    // glossy themed hub when premium, otherwise solid white
    ctx.beginPath();
    ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
    if (usePremiumStyle) {
      const grad = ctx.createRadialGradient(cx - hubR * 0.4, cy - hubR * 0.5, hubR * 0.1, cx, cy, hubR);
      grad.addColorStop(0, "rgba(255,255,255,0.85)");
      grad.addColorStop(0.45, themeTint);
      grad.addColorStop(1, shadeColor(themeTint, -0.25));
      ctx.fillStyle = grad;
    } else {
      ctx.fillStyle = "#ffffff";
    }
    ctx.fill();
    if (logoImgRef.current) {
      const img = logoImgRef.current;
      const target = hubR * 1.6;
      const ar = img.naturalWidth / Math.max(img.naturalHeight, 1);
      let w = target, h = target;
      if (ar > 1) h = target / ar;
      else w = target * ar;
      try {
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, hubR - 3, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(img, cx - w / 2, cy - h / 2, w, h);
        ctx.restore();
      } catch (_) {}
    } else {
      ctx.fillStyle = usePremiumStyle ? "#ffffff" : effectivePrimary;
      ctx.font = `700 ${Math.round(hubR * 0.7)}px var(--font-heading, 'Space Grotesk', sans-serif)`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("SPIN", cx, cy);
    }
    ctx.restore();
  };

  // Initial draw + on slices change
  useEffect(() => {
    draw(stateRef.current.angle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slices, palette, primaryColor, effectivePrimary, usePremiumStyle]);

  // Animation effect when spinningTo changes
  useEffect(() => {
    if (spinningTo == null || !slices.length) return;
    const n = slices.length;
    const arc = (Math.PI * 2) / n;

    // Pointer sits at the TOP of the wheel which, in canvas-radian land
    // (0 = 3 o'clock, increasing clockwise), is -π/2.
    // For slice i (drawn between start = θ + i*arc and start + arc) the
    // CENTER ends up at angle θ + i*arc + arc/2.
    // We want that center to be at the pointer, so:
    //   θ + i*arc + arc/2  ≡  -π/2   (mod 2π)
    //   θ_target          =  -π/2 − (i*arc + arc/2)   (mod 2π)
    const targetSliceCenter = spinningTo * arc + arc / 2;
    let targetMod = (-Math.PI / 2 - targetSliceCenter) % (Math.PI * 2);
    if (targetMod < 0) targetMod += Math.PI * 2;            // normalize to [0, 2π)

    const initial = stateRef.current.angle;
    const initialMod = ((initial % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    // Always rotate forward (clockwise visually): land on the target on the next pass
    let delta = targetMod - initialMod;
    if (delta <= 0) delta += Math.PI * 2;

    const turns = 6 + Math.floor(Math.random() * 3);        // MUST be integer so endAngle mod 2π == delta
    const endAngle = initial + delta + Math.PI * 2 * turns;

    const start = performance.now();
    const startAngle = initial;
    const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);

    const tick = (now) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / spinDuration, 1);
      const k = easeOutQuint(t);
      const cur = startAngle + (endAngle - startAngle) * k;
      stateRef.current.angle = cur;
      draw(cur);
      if (t < 1) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        stateRef.current.angle = endAngle;
        draw(endAngle);
        onSpinEnd && onSpinEnd();
      }
    };
    animRef.current = requestAnimationFrame(tick);
    return () => animRef.current && cancelAnimationFrame(animRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinningTo]);

  // Redraw on resize
  useEffect(() => {
    const onResize = () => draw(stateRef.current.angle);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative mx-auto w-[min(92vw,420px)] h-[min(92vw,420px)]">
      <canvas
        ref={canvasRef}
        className="w-full h-full rounded-full ring-1 ring-white/20"
        style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.22)" }}
        data-testid={testId}
      />
      {/* pointer at top */}
      <div
        data-testid="wheel-pointer"
        className="absolute left-1/2 -translate-x-1/2"
        style={{ top: -10 }}
      >
        <svg width="40" height="50" viewBox="0 0 40 50" aria-hidden="true">
          <defs>
            <linearGradient id="ptr" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor={effectivePrimary} />
            </linearGradient>
          </defs>
          <path
            d="M20 50 L0 18 Q0 0 20 0 Q40 0 40 18 Z"
            fill="url(#ptr)"
            stroke="rgba(0,0,0,0.18)"
            strokeWidth="1"
          />
        </svg>
      </div>
    </div>
  );
};

function readableTextColor(hex) {
  if (!hex || typeof hex !== "string") return "#0f172a";
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#0f172a" : "#ffffff";
}

// Lighten/darken a hex color by `amount` ∈ [-1, 1].
function shadeColor(hex, amount) {
  if (!hex || typeof hex !== "string") return "#000000";
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const apply = (c) =>
    Math.max(0, Math.min(255, Math.round(c + (amount > 0 ? (255 - c) * amount : c * amount))));
  const rr = apply(r).toString(16).padStart(2, "0");
  const gg = apply(g).toString(16).padStart(2, "0");
  const bb = apply(b).toString(16).padStart(2, "0");
  return `#${rr}${gg}${bb}`;
}

export default WheelCanvas;
